/**
 * Classes Hook
 * Manage instructor classes (create, list, update, delete)
 */

import { useState, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, ApiError } from '../services/api';
import type { Class, UpdateClassRequest, CreateClassRequest as SharedCreateClassRequest } from '@cloak-next/shared';

/**
 * Local interface for form data (compatible with CreateClassDialog)
 * Transforms to SharedCreateClassRequest before API call
 */
interface CreateClassFormData {
  className: string;
  shortClassId: string;
  startTime: string; // ISO date string
  endTime: string; // ISO date string
  maxStudents: number;
  classCode?: string;
  isDraft?: boolean;
  environmentId: string;
}

interface UseClassesResult {
  classes: Class[];
  loading: boolean;
  error: string | null;
  fetchClasses: () => Promise<void>;
  createClass: (data: CreateClassFormData) => Promise<Class>;
  updateClass: (className: string, updates: UpdateClassRequest) => Promise<Class>;
  deleteClass: (className: string) => Promise<void>;
}

/**
 * Hook for managing instructor classes
 */
export function useClasses(): UseClassesResult {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch instructor's classes
   */
  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet<{ success: boolean; data: Class[] }>('/classes');
      setClasses(response.data || []);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to fetch classes';
      setError(message);
      console.error('Failed to fetch classes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new class
   * Transforms form data to API format (SharedCreateClassRequest)
   */
  const createClass = useCallback(async (data: CreateClassFormData): Promise<Class> => {
    try {
      setLoading(true);
      setError(null);

      // Transform form data to API format
      const apiRequest: SharedCreateClassRequest = {
        className: data.className,
        shortClassId: data.shortClassId,
        startTime: data.startTime,
        endTime: data.endTime,
        maxStudents: data.maxStudents,
        classCode: data.classCode,
        isDraft: data.isDraft,
        environmentId: data.environmentId,
      };

      const response = await apiPost<{ success: boolean; data: Class }>('/classes', apiRequest);

      // Add to local state
      setClasses(prev => [...prev, response.data]);

      return response.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create class';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update a class
   */
  const updateClass = useCallback(async (className: string, updates: UpdateClassRequest): Promise<Class> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiPut<{ success: boolean; data: Class }>(`/classes/${encodeURIComponent(className)}`, updates);

      // Update local state
      setClasses(prev => prev.map(c => c.className === className ? response.data : c));

      return response.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update class';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a class
   */
  const deleteClass = useCallback(async (className: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await apiDelete(`/classes/${encodeURIComponent(className)}`);

      // Remove from local state
      setClasses(prev => prev.filter(c => c.className !== className));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete class';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    classes,
    loading,
    error,
    fetchClasses,
    createClass,
    updateClass,
    deleteClass,
  };
}
